import { useState, useRef, useEffect, useCallback } from "react";
import { Mail, Send, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/utils/cn";
import {
	getCatalog,
	previewEmail,
	sendEmail,
	PlaygroundCatalogEntry,
	PreviewResult,
} from "@/services/emailPlayground";
import { useToast } from "@/components/ui/use-toast";

const TO_ADDRESS_KEY = "emailPlayground.toAddress";

const GROUP_LABELS: Record<PlaygroundCatalogEntry["group"], string> = {
	notification: "Notification Events",
	transactional: "Transactional",
	report: "Reports",
};

const GROUP_ORDER: PlaygroundCatalogEntry["group"][] = [
	"notification",
	"transactional",
	"report",
];

export function EmailPlayground() {
	const { toast } = useToast();

	const [catalog, setCatalog] = useState<PlaygroundCatalogEntry[]>([]);
	const [selectedKey, setSelectedKey] = useState<string | null>(null);
	const [formData, setFormData] = useState<Record<string, unknown>>({});
	const [preview, setPreview] = useState<PreviewResult | null>(null);
	const [previewError, setPreviewError] = useState<string | null>(null);
	const [toAddress, setToAddress] = useState(
		() => localStorage.getItem(TO_ADDRESS_KEY) ?? "",
	);
	const [sending, setSending] = useState(false);
	const debounceRef = useRef<ReturnType<typeof setTimeout>>();
	const previewSeqRef = useRef(0);

	useEffect(() => {
		getCatalog()
			.then(setCatalog)
			.catch((e: Error) => {
				toast({
					title: "Failed to load catalog",
					description: e.message,
					variant: "destructive",
				});
			});

		return () => {
			clearTimeout(debounceRef.current);
		};
	}, [toast]);

	const runPreview = useCallback(
		(key: string, data: Record<string, unknown>) => {
			const seq = ++previewSeqRef.current;
			previewEmail(key, data)
				.then((r) => {
					if (seq !== previewSeqRef.current) return;
					setPreview(r);
					setPreviewError(null);
				})
				.catch((e: Error) => {
					if (seq !== previewSeqRef.current) return;
					setPreviewError(e.message);
					setPreview(null);
				});
		},
		[],
	);

	const selectEntry = useCallback(
		(entry: PlaygroundCatalogEntry) => {
			const samples = Object.fromEntries(
				entry.fields.map((f) => [f.name, f.sample]),
			);
			clearTimeout(debounceRef.current);
			setSelectedKey(entry.key);
			setFormData(samples);
			setPreview(null);
			setPreviewError(null);
			runPreview(entry.key, samples);
		},
		[runPreview],
	);

	const updateField = useCallback(
		(name: string, value: unknown) => {
			const next = { ...formData, [name]: value };
			setFormData(next);
			clearTimeout(debounceRef.current);
			debounceRef.current = setTimeout(() => {
				if (selectedKey) {
					runPreview(selectedKey, next);
				}
			}, 500);
		},
		[formData, selectedKey, runPreview],
	);

	const handleSend = useCallback(async () => {
		if (!selectedKey || !toAddress) return;
		setSending(true);
		try {
			const { messageId } = await sendEmail(selectedKey, formData, toAddress);
			localStorage.setItem(TO_ADDRESS_KEY, toAddress);
			toast({
				title: "Email sent",
				description: messageId ? `Message ID: ${messageId}` : `Sent to ${toAddress}`,
			});
		} catch (e: unknown) {
			toast({
				title: "Send failed",
				description: e instanceof Error ? e.message : "Unknown error",
				variant: "destructive",
			});
		} finally {
			setSending(false);
		}
	}, [selectedKey, formData, toAddress, toast]);

	const selectedEntry = catalog.find((e) => e.key === selectedKey) ?? null;

	const groupedCatalog = GROUP_ORDER.map((group) => ({
		group,
		label: GROUP_LABELS[group],
		entries: catalog.filter((e) => e.group === group),
	})).filter((g) => g.entries.length > 0);

	return (
		<div className="flex flex-col h-full space-y-0">
			{/* Header */}
			<div className="flex items-center justify-between mb-6">
				<div>
					<h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
						<Mail className="w-6 h-6" />
						Email Playground
					</h1>
					<p className="text-gray-500 mt-1">
						Preview and send templated emails with custom field data
					</p>
				</div>
			</div>

			{/* Main layout */}
			<div className="flex gap-6 flex-1 min-h-0">
				{/* Sidebar */}
				<div
					className="w-64 shrink-0 flex flex-col gap-1 overflow-y-auto"
					style={{ minWidth: "16rem", maxWidth: "16rem" }}
				>
					{groupedCatalog.map(({ group, label, entries }) => (
						<div key={group} className="mb-2">
							<p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-2 mb-1">
								{label}
							</p>
							{entries.map((entry) => (
								<button
									type="button"
									key={entry.key}
									onClick={() => selectEntry(entry)}
									className={cn(
										"w-full text-left px-3 py-2 rounded-md text-sm transition-colors",
										selectedKey === entry.key
											? "bg-blue-600 text-white font-medium"
											: "text-gray-700 hover:bg-gray-100",
									)}
								>
									{entry.label}
								</button>
							))}
						</div>
					))}
					{catalog.length === 0 && (
						<p className="text-sm text-gray-400 px-2 py-4">Loading catalog…</p>
					)}
				</div>

				{/* Right column */}
				<div className="flex-1 flex flex-col gap-4 min-w-0">
					{selectedEntry ? (
						<>
							{/* Form card */}
							<Card>
								<CardHeader>
									<CardTitle>{selectedEntry.label}</CardTitle>
								</CardHeader>
								<CardContent className="space-y-4">
									{/* Fields grid */}
									{selectedEntry.fields.length > 0 && (
										<div className="grid grid-cols-2 gap-4">
											{selectedEntry.fields.map((field) => (
												<div key={field.name} className="space-y-1">
													<Label htmlFor={`field-${field.name}`}>
														{field.label}
													</Label>
													{field.type === "boolean" ? (
														<div className="flex items-center gap-2 pt-1">
															<input
																id={`field-${field.name}`}
																type="checkbox"
																checked={Boolean(formData[field.name])}
																onChange={(e) =>
																	updateField(field.name, e.target.checked)
																}
																className="h-4 w-4 rounded border-gray-300 text-blue-600"
															/>
															<span className="text-sm text-gray-600">
																{formData[field.name] ? "true" : "false"}
															</span>
														</div>
													) : field.type === "number" ? (
														<Input
															id={`field-${field.name}`}
															type="number"
															value={String(formData[field.name] ?? "")}
															onChange={(e) =>
																updateField(field.name, Number(e.target.value))
															}
														/>
													) : field.type === "date" ? (
														<Input
															id={`field-${field.name}`}
															type="date"
															value={String(formData[field.name] ?? "")}
															onChange={(e) =>
																updateField(field.name, e.target.value)
															}
														/>
													) : (
														<Input
															id={`field-${field.name}`}
															type="text"
															value={String(formData[field.name] ?? "")}
															onChange={(e) =>
																updateField(field.name, e.target.value)
															}
														/>
													)}
												</div>
											))}
										</div>
									)}

									{/* Send row */}
									<div className="flex items-end gap-3 pt-2 border-t">
										<div className="flex-1 space-y-1">
											<Label htmlFor="send-to">Send to</Label>
											<Input
												id="send-to"
												type="email"
												placeholder="recipient@example.com"
												value={toAddress}
												onChange={(e) => setToAddress(e.target.value)}
											/>
										</div>
										<Button
											type="button"
											onClick={handleSend}
											disabled={sending || !toAddress}
										>
											{sending ? (
												<Loader2 className="w-4 h-4 mr-2 animate-spin" />
											) : (
												<Send className="w-4 h-4 mr-2" />
											)}
											Send
										</Button>
									</div>
								</CardContent>
							</Card>

							{/* Preview area */}
							<Card className="flex-1 flex flex-col min-h-0">
								<CardHeader className="shrink-0">
									<CardTitle>
										{preview ? (
											<span className="text-sm font-normal text-gray-600">
												<span className="font-semibold text-gray-900">Subject:</span>{" "}
												{preview.subject}
											</span>
										) : (
											"Preview"
										)}
									</CardTitle>
								</CardHeader>
								<CardContent className="flex-1 min-h-0 p-0">
									{previewError ? (
										<div className="m-4 p-4 rounded-md bg-red-50 border border-red-200">
											<p className="text-sm font-medium text-red-700 mb-1">
												Preview error
											</p>
											<p className="text-sm text-red-600">{previewError}</p>
										</div>
									) : preview ? (
										<iframe
											sandbox=""
											srcDoc={preview.html}
											className="w-full h-full border-0 rounded-b-lg"
											style={{ minHeight: "400px" }}
											title="Email preview"
										/>
									) : (
										<div className="flex items-center justify-center h-64 text-gray-400">
											<p className="text-sm">Select a template to see preview</p>
										</div>
									)}
								</CardContent>
							</Card>
						</>
					) : (
						<div className="flex-1 flex items-center justify-center text-gray-400">
							<div className="text-center">
								<Mail className="w-12 h-12 mx-auto mb-4 opacity-30" />
								<p>Select an email template from the sidebar</p>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
